import React, { useEffect, useRef, useState } from 'react';
import { m } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { SectionTag } from '../ui/SectionTag';
import { galleryItems } from '../../data/productGallery';
export function ProductGallery() {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [visibleIndexes, setVisibleIndexes] = useState<Set<number>>(new Set([0]));
  const activeIndex = visibleIndexes.size ? Math.min(...visibleIndexes) : 0;
  useEffect(() => {
    const root = scrollerRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(entries => {
      setVisibleIndexes(prev => {
        const next = new Set(prev);
        entries.forEach(entry => {
          const idx = Number((entry.target as HTMLElement).dataset.index);
          if (entry.isIntersecting) {
            next.add(idx);
          } else {
            next.delete(idx);
          }
        });
        return next;
      });
    }, {
      root,
      threshold: 0.6
    });
    cardRefs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);
  function scrollToIndex(index: number) {
    cardRefs.current[index]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'start',
      block: 'nearest'
    });
  }
  const goPrev = () => scrollToIndex(Math.max(activeIndex - 1, 0));
  const goNext = () => scrollToIndex(Math.min(activeIndex + 1, galleryItems.length - 1));
  return <section className="bg-bg-secondary py-14 sm:py-16" data-id="element-191" data-mp="1e6v35movmlkx">
      <div className="mx-auto max-w-3xl px-6 text-center sm:px-8" data-id="element-192" data-mp="1j5eq9qf7i3ev">
        <SectionTag icon="👀" data-id="element-193" data-mp="1o2y6uwzdms9z">Mira cómo es por dentro</SectionTag>
        <h2 className="mt-5 font-heading text-[28px] font-bold uppercase text-marino sm:text-[36px]" data-id="element-194" data-mp="1o13qzx2ypqcr">
          Mira cómo es por dentro
        </h2>
        <p className="mt-4 text-base leading-relaxed text-gris-texto sm:text-lg" data-id="element-195" data-mp="3ptrpxu8kprmq">
          Del objetivo al montaje, del esquema a la pista. Explora páginas reales de los ejercicios, las
          sesiones y los registros que incluye la colección.
        </p>
      </div>

      <div className="relative mx-auto mt-10 max-w-6xl px-6 sm:px-8" data-id="element-196" data-mp="3rs1cuytdgnbq">
        <button type="button" onClick={goPrev} disabled={activeIndex === 0} aria-label="Página anterior" className="absolute -left-2 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-gris-claro bg-white text-marino shadow-media transition-opacity disabled:opacity-30 sm:-left-4 sm:flex" data-id="element-197" data-mp="3rpyftutsxtxi">
          <ChevronLeftIcon size={20} data-id="element-198" data-mp="ef964cihzp1r" />
        </button>
        <button type="button" onClick={goNext} disabled={activeIndex === galleryItems.length - 1} aria-label="Página siguiente" className="absolute -right-2 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-gris-claro bg-white text-marino shadow-media transition-opacity disabled:opacity-30 sm:-right-4 sm:flex" data-id="element-199" data-mp="9dsaavevess1">
          <ChevronRightIcon size={20} data-id="element-200" data-mp="16z3864d98iyn" />
        </button>

        <div ref={scrollerRef} className="scrollbar-hide flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-2" data-id="element-201" data-mp="t0d8qi2yq774">
          {galleryItems.map((item, i) => <m.div key={item.image} ref={el => cardRefs.current[i] = el} data-index={i} initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i % 2 * 0.06
        }} className="w-[80%] flex-shrink-0 snap-start sm:w-[calc(50%-8px)]" data-id="element-202" data-mp="j9ilbadzd1k3" data-mpi={i}>
              <div className="overflow-hidden rounded-2xl border border-gris-claro bg-white shadow-leve" data-id="element-203" data-mp="chinw7wfdjb">
                <img src={item.image} alt={item.title} width={496} height={702} loading="lazy" decoding="async" className="h-auto w-full object-contain" data-id="element-204" data-mp="59rfvy1j4thp" />
              </div>
              <h3 className="mt-3 font-heading text-base font-semibold text-marino" data-id="element-205" data-mp="ytak94d2ataa">{item.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-gris-texto" data-id="element-206" data-mp="tu6aocm86tjf">{item.text}</p>
            </m.div>)}
        </div>

        <div className="mt-6 flex items-center justify-center gap-4" data-id="element-207" data-mp="kv2rz9qz0g5g">
          <div className="flex items-center gap-2" data-id="element-208" data-mp="kxyr31ek1avh">
            {galleryItems.map((item, i) => <button key={item.image} type="button" onClick={() => scrollToIndex(i)} aria-label={`Ir a la página ${i + 1}`} aria-current={activeIndex === i} className={`h-2 rounded-full transition-all duration-200 ${activeIndex === i ? 'w-6 bg-naranja-strong' : 'w-2 bg-gris-claro'}`} data-id="element-209" data-mp="fyccoomzpol3" data-mpi={i} />)}
          </div>
          <span className="text-sm font-medium text-gris-muted" data-id="element-210" data-mp="2hsqk96ntd7i3">
            {activeIndex + 1} de {galleryItems.length}
          </span>
        </div>
      </div>

      <p className="mt-6 text-center text-sm text-gris-muted" data-id="element-211" data-mp="2rke7evdc82dr">Desliza para explorar</p>
      <p className="mx-auto mt-2 max-w-xl px-6 text-center text-xs leading-relaxed text-gris-muted sm:px-8" data-id="element-212" data-mp="1tw46kjpafko8">
        Estas imágenes son muestras del contenido incluido, no productos adicionales. Las páginas 4 y 5
        muestran las dos partes de una misma sesión.
      </p>
    </section>;
}